// TestimonialCarousel.jsx
import React, { useState, useEffect, useRef } from 'react';
import '../Testimonials/TestimonialCarousel.css';
import nonTradImage from '../Assets/20240219_134911.png';
import fedWorkerImage from '../Assets/Gemini_Generated_Image_exztbrexztbrexzt.png';
import unnamed_whiteHouse from '../Assets/unnamed_whiteHouse.jpg';
import JoseLeiva from '../Assets/JoseLeiva.png'
import author from '../Assets/selfPublished_author.png';
import boho from '../Assets/boho.jpg';
import fastFood from '../Assets/fastFood.png';

const TestimonialCarousel = ({ display = 'all' }) => { // 'all', 'hookA', 'hookB', etc.
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timeoutRef = useRef(null);
  const carouselRef = useRef(null);

  const allTestimonials = [
    {
      id: 1,
      name: "Public School Teacher",
      role: "Georgia, United States",
      image: "https://randomuser.me/api/portraits/men/75.jpg",
      testimonial: "They organized my evidence and cited the right laws, which led to the EEOC investigating my complaint. When the EEOC declined to litigate my case and issued a Notice of Right to Sue, they clearly explained my options—since I couldn't afford a lawyer.",
      contexts: ['all', 'hookB'] // Remove 'hookC' if you DON'T want it in HookC
    },
    {
      id: 2,
      name: "Highschool Graduate",
      role: "Honduras, Central America",
      image: "https://randomuser.me/api/portraits/men/37.jpg",
      testimonial: "Their SAT resources helped me earn a college-ready score and get in. I already had someone helping with my F-1 visa, but knowing I could fall back on them took a lot of stress off.",
      contexts: ['all', 'hookB'] // Remove 'hookC' if you DON'T want it in HookC
    },
    {
      id: 3,
      name: "Federal Employee",
      role: "Arizona, United States",
      image: fedWorkerImage,
      testimonial: "I was worried about facing the Merit Systems Protection Board alone, but they provided a non-attorney representative who spoke for me, questioned witnesses, and presented the facts—at no extra cost.",
      contexts: ['all', 'hookB'] // Remove 'hookC' if you DON'T want it in HookC                           
    },
    {
      id: 4,
      name: "Non-Traditional Student",
      role: "California, United States",
      image: nonTradImage,
      testimonial: "I didn’t graduate high school, but they helped me get my documents ready on the first try. Now I’m using my SAT score to skip the GED and start college-level courses online.",
      contexts: ['all', 'hookB'] // Remove 'hookC' if you DON'T want it in HookC
    },
    {
      id: 5,
      name: "International Student",
      role: "Honduras, Central America",
      image: JoseLeiva,
      testimonial: "They helped me boost my SAT score and win the Hispanic Scholarship Fund. Now I feel ready for my first semester at the University of South Florida — best decision ever!",
      contexts: ['all', 'hookB'] // Remove 'hookC' if you DON'T want it in HookC
    },
    {
      id: 6,
      name: "Policy Advisor",
      role: "Kentucky, United States",
      image: unnamed_whiteHouse,
      testimonial: "Their service works! I got placed in the Office of the Surgeon General—my essay spoke to people in high places I didn’t even know existed, and things moved fast.",
      contexts: ['all', 'hookB', 'hookC'] // This one shows in HookC
    },
    {
      id: 7,
      name: "Self-Published Author",
      role: "Texas, United States",
      image: author, 
      testimonial: "I got my ebook back full of errors. They edited and formatted it—now it’s ready to sell.",
      contexts: ['all', 'hookC']
    },
    {
      id: 8,
      name: "Corporate Professional",
      role: "South Carolina, United States",
      image: boho, 
      testimonial: "My usajobs.gov applications weren’t getting results. They revised my resume to highlight the exact skills and qualifications, boosting my chances of getting noticed.",
      contexts: ['all', 'hookC']
    },
    {
      id: 9,
      name: "College Graduate",
      role: "Arkansas, United States",
      image: fastFood,
      testimonial: "I thought I wasn’t qualified for a government job. They highlighted my degree and optimized my resume for usajobs.gov.",
      contexts: ['all', 'hookC']
    }
  ];

  // Filter testimonials based on display prop 
  const testimonials = allTestimonials.filter(t => 
    t.contexts.includes(display)
  );
  const nextSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  const resetTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const startTimer = () => {
    resetTimer();
    if (!isPaused && testimonials.length > 0) {
      timeoutRef.current = setTimeout(() => {
        nextSlide();
        startTimer();
      }, 6000);
    }
  };

  useEffect(() => {
    if (testimonials.length > 0) {
      startTimer();
    }
    return () => resetTimer();
  }, [currentIndex, startTimer, isPaused, testimonials.length]);

  const handleCarouselClick = () => {
    setIsPaused(!isPaused);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.code === 'Space' && document.activeElement === carouselRef.current) {
        e.preventDefault();
        setIsPaused(!isPaused);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isPaused]);

  // Don't render if no testimonials match the context
  if (testimonials.length === 0) {
    return null;
  }

  return (
    <div className="testimonial-carousel-container">
      <div className="testimonial-header">
        <h2>What Our Clients Say</h2>
        <div className="pause-indicator">
          {isPaused ? (
            <span className="paused-badge">⏸ Paused - Click to Play</span>
          ) : (
            <span className="playing-badge">▶ Auto-playing</span>
          )}
        </div>
      </div>

      <div 
        className="testimonial-carousel"
        ref={carouselRef}
        onClick={handleCarouselClick}
        tabIndex={0}
        role="region"
        aria-label="Testimonial carousel"
        aria-live={isPaused ? "off" : "polite"}
      >
        <div 
          className="carousel-track"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
            transition: 'transform 0.8s ease-in-out'
          }}
        >
          {testimonials.map((testimonial) => (
            <div key={testimonial.id} className="testimonial-slide">
              <div className="testimonial-content">
                <div className="testimonial-author">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="author-image"
                    loading="lazy"
                  />
                  <div className="author-info">
                    <h4>{testimonial.name}</h4>
                    <p>{testimonial.role}</p>
                  </div>
                </div>
               
                <div className="testimonial-text">{testimonial.testimonial}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="carousel-progress">
          {testimonials.map((_, index) => (
            <div 
              key={index}
              className={`progress-dot ${index === currentIndex ? 'active' : ''}`}
              style={{
                width: index === currentIndex ? '30px' : '12px',
                transition: 'width 0.3s ease'
              }}
            />
          ))}
        </div>

        <div className="click-hint">
          Click anywhere on the carousel to {isPaused ? 'play' : 'pause'}
        </div>
      </div>
    </div>
  );
};

export default TestimonialCarousel;